import {find} from 'lodash';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, RequiredMark} from 'components';
import {useUserSession} from 'hooks';
import {SessionActivityName, SessionActivityTextChatData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {
    PreSessionFlowState, PreSessionFlowStepState, PreSessionStep, PreSessionStepStatus, PreSessionStepType,
} from 'types';
import {buildTranslationKey} from 'utils/transform';

import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.PRE_SESSION_FLOW;
const ASYNC_REQUEST_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.ASYNC_REQUEST.TYPE;

const STATUS_ICON = {
    [PreSessionStepStatus.Pending]: {name: 'hourglass', color: 'default'},
    [PreSessionStepStatus.InProgress]: {name: 'hourglass', color: 'primary'},
    [PreSessionStepStatus.Done]: {name: 'check-circle', color: 'success'},
    [PreSessionStepStatus.Failed]: {name: 'cross-line', color: 'alert'},
    [PreSessionStepStatus.SkippedAfterRetries]: {name: 'alert-triangle', color: 'warning'},
} as const;

type StepRowProps = {
    step: PreSessionStep;
    state: PreSessionFlowStepState;
};

const StepRow: React.FC<StepRowProps> = ({step, state}) => {
    const {t} = useTranslation();
    const icon = STATUS_ICON[state.status];
    const collected = state.collected;
    const formEntries = useMemo(() => {
        if (!collected?.form || step.type !== PreSessionStepType.Form) {
            return [];
        }
        const fields = step.form?.fields ?? [];
        return fields
            // Prefer the field's human label (title) over the raw userInfo path (label).
            .map((f) => ({id: f.id, label: f.title?.trim() || f.label, value: collected.form?.[f.id] ?? ''}))
            .filter((e) => e.value);
    }, [collected, step]);

    const asyncRequestLabel = useMemo(() => {
        const ref = collected?.asyncRequestRef;
        if (!ref) {
            return '';
        }
        const tKey = ASYNC_REQUEST_T_KEYS[buildTranslationKey(ref)];
        return tKey ? t(tKey) : ref;
    }, [collected?.asyncRequestRef, t]);

    return (
        <div className='tw-flex tw-flex-col tw-gap-1 tw-py-2 tw-border-b tw-border-default-light'>
            <div className='tw-flex tw-items-center tw-gap-2'>
                <Icon name={icon.name} color={icon.color as any} size='sm' />
                <div className='tw-font-bold tw-text-base'>
                    {step.name}
                    {step.required && <RequiredMark />}
                </div>
            </div>
            <div className='tw-text-sm tw-text-default-dimmed tw-pl-6'>
                {t(T_KEYS.STATUS_LABEL[state.status])}
            </div>
            {formEntries.length > 0 && (
                <ul className='tw-pl-6 tw-mt-1 tw-text-sm'>
                    {formEntries.map((entry) => (
                        <li key={entry.id}>
                            <span className='tw-font-medium'>
                                {entry.label}
                                {':'}
                            </span>
                            {' '}
                            {entry.value}
                        </li>
                    ))}
                </ul>
            )}
            {collected?.asyncRequestRef && (
                <div className='tw-pl-6 tw-mt-1 tw-text-sm tw-text-default-dimmed'>
                    {t(T_KEYS.VERIFICATION_REF)}
                    {': '}
                    {asyncRequestLabel}
                </div>
            )}
            {collected?.screenshotRef && (
                <div className='tw-pl-6 tw-mt-1 tw-text-sm tw-text-default-dimmed'>
                    {t(T_KEYS.SCREENSHOT_REF)}
                    {step.screenshot?.name ? `: ${step.screenshot.name}` : ''}
                </div>
            )}
        </div>
    );
};

/**
 * Sidebar panel showing the pre-session-flow snapshot for the active TextChat or Avatar
 * activity. Reads activity.data.preSessionFlow and renders each step's status + collected data
 * (form values, screenshot ref, async-request ref).
 */
export const PreSessionFlowSidebar: React.FC = () => {
    const {t} = useTranslation();
    const {session} = useUserSession();

    const flowState = useMemo<PreSessionFlowState | null>(() => {
        const activity = find(session?.activities, (a) =>
            a.name === SessionActivityName.TextChat || a.name === SessionActivityName.Avatar);
        const data = activity?.data as SessionActivityTextChatData & {preSessionFlow?: PreSessionFlowState};
        return data?.preSessionFlow ?? null;
    }, [session]);

    return (
        <SidebarContent
            view={SidebarView.PreSessionFlow}
            header={<div className='tw-ts-h5'>{t(T_KEYS.TITLE)}</div>}
        >
            <div className='tw-mt-4 tw-pr-4 tw-pb-4 tw-overflow-y-auto tw-h-full'>
                {!flowState && (
                    <p className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.NO_FLOW)}</p>
                )}
                {flowState && flowState.flowSnapshot.steps.map((step) => {
                    const state = flowState.steps.find((s) => s.stepId === step.id);
                    if (!state) {
                        return null;
                    }
                    return <StepRow key={step.id} step={step} state={state} />;
                })}
            </div>
        </SidebarContent>
    );
};
